<template>
  <div
    class="action-card language-card"
    :class="{ hovered: isHovered, 'mobile-web': isMobileWeb }"
    @click="toggleOptions"
    @mouseenter="$emit('hover', true)"
    @mouseleave="$emit('hover', false)"
  >
    <div class="card-header">
      <IonIcon :icon="languageOutline" class="card-icon" />
      <span class="card-title">{{ $t('common.language') }}</span>
    </div>
    <div class="card-content">
      <div class="current-language">
        <span class="language-flag">{{ currentFlag }}</span>
        <span class="language-name">{{ currentName }}</span>
      </div>
      <IonIcon :icon="showOptions ? chevronUpOutline : chevronDownOutline" class="expand-icon" />
    </div>

    <!-- Language Options Overlay -->
    <div v-if="showOptions" class="language-overlay">
      <div
        v-for="locale in locales"
        :key="locale.code"
        class="language-option"
        :class="{ selected: locale.code === currentLocale }"
        @click.stop="selectLocale(locale.code)"
      >
        <span class="language-flag">{{ locale.flag }}</span>
        <span class="language-name">{{ locale.name }}</span>
        <IonIcon
          v-if="locale.code === currentLocale"
          :icon="checkmarkOutline"
          class="selected-icon"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { IonIcon } from '@ionic/vue';
import {
  checkmarkOutline,
  chevronDownOutline,
  chevronUpOutline,
  languageOutline,
} from 'ionicons/icons';

interface Locale {
  code: string;
  name: string;
  flag: string;
}

const props = defineProps<{
  locales: Locale[];
  currentLocale: string;
  currentFlag: string;
  currentName: string;
  isHovered?: boolean;
  isMobileWeb?: boolean;
}>();

const emit = defineEmits<{
  select: [code: string];
  hover: [hovered: boolean];
}>();

const showOptions = ref(false);

const toggleOptions = () => {
  showOptions.value = !showOptions.value;
};

const selectLocale = (code: string) => {
  emit('select', code);
  showOptions.value = false;
};

// Expose for parent to close options
defineExpose({
  closeOptions: () => {
    showOptions.value = false;
  },
});
</script>

<style scoped>
.action-card {
  background: var(--ion-card-background);

  border-radius: 12px;
  padding: 15px;
  cursor: pointer;
  box-shadow: var(--ion-box-shadow);
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  height: 80px;
  width: 250px;
  z-index: 2;
}

.action-card:hover {
  border: 1px solid var(--ion-input-border-color);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.card-icon {
  font-size: 24px;
  color: var(--ion-color-primary);
}

.card-title {
  font-weight: 600;
  font-size: 16px;
  color: var(--ion-text-color);
}

.card-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--ion-text-color);
}

.current-language {
  display: flex;
  align-items: center;
  gap: 8px;
}

.language-flag {
  font-size: 20px;
}

.language-name {
  font-size: 14px;
  color: var(--ion-text-color);
}

.expand-icon {
  font-size: 18px;
  color: var(--ion-text-color);
}

.language-overlay {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--ion-background-color-primary);
  border-radius: 8px;
  box-shadow: var(--ion-box-shadow-hover);
  z-index: 9999;
  overflow: hidden;
  margin-top: 4px;
}

.language-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.language-option:hover {
  background: var(--ion-background-color-secondary);
}

.language-option.selected {
  background: var(--ion-color-primary-tint);
  color: var(--ion-color-primary);
}

.selected-icon {
  color: var(--ion-color-primary);
  font-size: 16px;
  margin-left: auto;
}

/* Mobile styles */
.action-card.mobile-web {
  width: 100%;
  height: auto;
  min-height: 60px;
}
</style>
